import { useState } from 'react';
import { useCategories } from '../hooks/useCategories';

export default function CategoriesPage() {
  const { categories, loading, error, createCategory, updateCategory, deleteCategory, refresh } = useCategories();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<any>(null);

  const handleCreateCategory = async (data: any) => {
    const result = await createCategory(data);
    if (result) {
      setShowCreateModal(false);
      refresh();
    }
  };

  const handleUpdateCategory = async (id: string, data: any) => {
    const result = await updateCategory(id, data);
    if (result) {
      setEditingCategory(null);
      refresh();
    }
  };

  const handleDeleteCategory = async (id: string) => {
    if (window.confirm('确定要删除这个分类吗？')) {
      const success = await deleteCategory(id);
      if (success) {
        refresh();
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">加载中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="categories-page">
      <div className="categories-header">
        <h1>📁 分类管理</h1>
        <button onClick={() => setShowCreateModal(true)} className="create-btn">
          + 创建分类
        </button>
      </div>

      <div className="categories-grid">
        {categories.map((category) => (
          <div key={category.id} className="category-card">
            <div className="category-icon" style={{ color: category.color || '#666' }}>
              {category.icon || '📁'}
            </div>
            <div className="category-info">
              <h3>{category.name}</h3>
              <p className="category-type">{category.type}</p>
              <p className="category-count">{category.count || 0} 项</p>
            </div>
            <div className="category-actions">
              <button onClick={() => setEditingCategory(category)} className="edit-btn">
                编辑
              </button>
              <button onClick={() => handleDeleteCategory(category.id)} className="delete-btn">
                删除
              </button>
            </div>
          </div>
        ))}
      </div>

      {showCreateModal && (
        <CategoryModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateCategory}
        />
      )}

      {editingCategory && (
        <CategoryModal
          category={editingCategory}
          onClose={() => setEditingCategory(null)}
          onSave={(data) => handleUpdateCategory(editingCategory.id, data)}
        />
      )}

      <style>{`
        .categories-page {
          padding: 24px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .categories-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .categories-header h1 {
          font-size: 28px;
          font-weight: 700;
          margin: 0;
          color: #333;
        }

        .create-btn {
          padding: 10px 20px;
          background: #007bff;
          color: white;
          border: none;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: background 0.2s;
        }

        .create-btn:hover {
          background: #0056b3;
        }

        .categories-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }

        .category-card {
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          display: flex;
          flex-direction: column;
          gap: 12px;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .category-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .category-icon {
          font-size: 48px;
          text-align: center;
        }

        .category-info {
          text-align: center;
        }

        .category-info h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #333;
        }

        .category-type {
          font-size: 12px;
          color: #999;
          margin: 0 0 4px 0;
          text-transform: uppercase;
        }

        .category-count {
          font-size: 14px;
          color: #666;
          margin: 0;
        }

        .category-actions {
          display: flex;
          gap: 8px;
          margin-top: auto;
        }

        .edit-btn,
        .delete-btn {
          flex: 1;
          padding: 8px;
          border: none;
          border-radius: 6px;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .edit-btn {
          background: #f0f0f0;
          color: #333;
        }

        .edit-btn:hover {
          background: #e0e0e0;
        }

        .delete-btn {
          background: #fee;
          color: #c00;
        }

        .delete-btn:hover {
          background: #fdd;
        }

        @media (max-width: 768px) {
          .categories-page {
            padding: 16px;
          }

          .categories-header h1 {
            font-size: 24px;
          }

          .categories-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

interface CategoryModalProps {
  category?: any;
  onClose: () => void;
  onSave: (data: any) => void;
}

function CategoryModal({ category, onClose, onSave }: CategoryModalProps) {
  const [name, setName] = useState(category?.name || '');
  const [type, setType] = useState(category?.type || 'bookmark');
  const [icon, setIcon] = useState(category?.icon || '📁');
  const [color, setColor] = useState(category?.color || '#007bff');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ name, type, icon, color });
  };

  const commonIcons = ['📁', '📚', '💡', '⭐', '🔒', '⏰', '📝', '🎯', '💼', '🏠'];
  const commonColors = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6f42c1', '#fd7e14', '#20c997', '#6c757d'];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{category ? '编辑分类' : '创建分类'}</h2>
          <button onClick={onClose} className="close-btn">×</button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label>名称</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="输入分类名称"
              required
            />
          </div>

          <div className="form-group">
            <label>类型</label>
            <select value={type} onChange={(e) => setType(e.target.value)} required>
              <option value="bookmark">书签</option>
              <option value="note">笔记</option>
              <option value="expense">支出</option>
              <option value="schedule">日程</option>
              <option value="reminder">提醒</option>
            </select>
          </div>

          <div className="form-group">
            <label>图标</label>
            <div className="icon-picker">
              {commonIcons.map((i) => (
                <button
                  key={i}
                  type="button"
                  className={`icon-option ${icon === i ? 'selected' : ''}`}
                  onClick={() => setIcon(i)}
                >
                  {i}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              placeholder="或输入自定义图标"
            />
          </div>

          <div className="form-group">
            <label>颜色</label>
            <div className="color-picker">
              {commonColors.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`color-option ${color === c ? 'selected' : ''}`}
                  style={{ background: c }}
                  onClick={() => setColor(c)}
                />
              ))}
            </div>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
            />
          </div>

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-btn">
              取消
            </button>
            <button type="submit" className="save-btn">
              保存
            </button>
          </div>
        </form>

        <style>{`
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
            padding: 20px;
          }

          .modal {
            background: white;
            border-radius: 12px;
            max-width: 500px;
            width: 100%;
            max-height: 90vh;
            overflow-y: auto;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          }

          .modal-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 20px;
            border-bottom: 1px solid #e0e0e0;
          }

          .modal-header h2 {
            margin: 0;
            font-size: 20px;
            font-weight: 600;
            color: #333;
          }

          .close-btn {
            background: none;
            border: none;
            font-size: 32px;
            color: #999;
            cursor: pointer;
            padding: 0;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            transition: all 0.2s;
          }

          .close-btn:hover {
            background: #f0f0f0;
            color: #666;
          }

          .modal-form {
            padding: 20px;
          }

          .form-group {
            margin-bottom: 20px;
          }

          .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: #333;
          }

          .form-group input,
          .form-group select {
            width: 100%;
            padding: 10px;
            border: 1px solid #e0e0e0;
            border-radius: 6px;
            font-size: 14px;
            outline: none;
          }

          .form-group input:focus,
          .form-group select:focus {
            border-color: #007bff;
          }

          .icon-picker,
          .color-picker {
            display: flex;
            gap: 8px;
            margin-bottom: 8px;
            flex-wrap: wrap;
          }

          .icon-option {
            width: 48px;
            height: 48px;
            border: 2px solid #e0e0e0;
            border-radius: 8px;
            background: white;
            font-size: 24px;
            cursor: pointer;
            transition: all 0.2s;
          }

          .icon-option:hover {
            border-color: #007bff;
            transform: scale(1.1);
          }

          .icon-option.selected {
            border-color: #007bff;
            background: #e3f2fd;
          }

          .color-option {
            width: 40px;
            height: 40px;
            border: 2px solid #e0e0e0;
            border-radius: 50%;
            cursor: pointer;
            transition: all 0.2s;
          }

          .color-option:hover {
            transform: scale(1.1);
          }

          .color-option.selected {
            border-color: #333;
            border-width: 3px;
          }

          .modal-actions {
            display: flex;
            gap: 12px;
            margin-top: 24px;
          }

          .cancel-btn,
          .save-btn {
            flex: 1;
            padding: 12px;
            border: none;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .cancel-btn {
            background: #f0f0f0;
            color: #333;
          }

          .cancel-btn:hover {
            background: #e0e0e0;
          }

          .save-btn {
            background: #007bff;
            color: white;
          }

          .save-btn:hover {
            background: #0056b3;
          }
        `}</style>
      </div>
    </div>
  );
}
